import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  message,
  Row,
  Col,
  Statistic,
  Divider,
  Table,
  Popconfirm,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  CarOutlined,
  ToolOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { COLOR_PRIMARY, COLOR_SUCCESS } from '../../utils/colors';

const { Column } = Table;

const VanDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [van, setVan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalJobs: 0,
    completedJobs: 0,
    activeJobs: 0,
  });

  const fetchVanDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.vans.getById(id);
      console.log('Van response:', response);

      const vanData = response.data?.data || response.data;
      if (!vanData) {
        throw new Error('Van not found');
      }

      setVan(vanData);
      
      // Use the stats from the backend
      if (vanData.stats) {
        setStats({
          totalJobs: vanData.stats.totalJobs || 0,
          completedJobs: vanData.stats.completedJobs || 0,
          activeJobs: vanData.stats.activeJobs || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching van details:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load van details');
      message.error('Failed to load van details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVanDetails();
  }, [fetchVanDetails]);

  const handleDelete = async () => {
    try {
      await apiService.vans.delete(id);
      message.success('Van deleted successfully');
      navigate('/vans');
    } catch (error) {
      console.error('Error deleting van:', error);
      message.error('Failed to delete van');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <Space>
            <CarOutlined />
            <span>Van Details</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/vans/edit/${id}`)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this van?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />}>Delete</Button>
            </Popconfirm>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/vans')}
            >
              Back to List
            </Button>
          </Space>
        }
      >
        {van && (
          <>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Jobs"
                    value={stats.totalJobs}
                    prefix={<CarOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Completed Jobs"
                    value={stats.completedJobs}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: COLOR_SUCCESS }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Assigned Jobs"
                    value={stats.activeJobs}
                    prefix={<CarOutlined />}
                    valueStyle={{ color: COLOR_PRIMARY }}
                  />
                </Card>
              </Col>
            </Row>

            <Divider orientation="left">Vehicle Information</Divider>
            
            <Descriptions bordered>
              <Descriptions.Item label="Registration" span={3}>
                <Space>
                  <CarOutlined />
                  {van.registration}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Make & Model" span={3}>
                {`${van.make} ${van.model}`}
              </Descriptions.Item>
              <Descriptions.Item label="Year" span={3}>
                {van.year}
              </Descriptions.Item>
              <Descriptions.Item label="Capacity" span={3}>
                {van.capacity}
              </Descriptions.Item>
              <Descriptions.Item label="Status" span={3}>
                <Tag color={
                  van.status === 'available' ? 'success' :
                  van.status === 'in-use' ? 'processing' :
                  'warning'
                }>
                  {van.status?.toUpperCase() || 'N/A'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">
              <Space>
                <ToolOutlined />
                <span>Maintenance Information</span>
              </Space>
            </Divider>

            <Descriptions bordered>
              <Descriptions.Item label="Last Maintenance" span={3}>
                <Space>
                  <CalendarOutlined />
                  {formatDate(van.lastMaintenance)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Next Service" span={3}>
                <Space>
                  <CalendarOutlined />
                  {formatDate(van.nextService)}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">
              <Space>
                <HistoryOutlined />
                <span>Maintenance History</span>
              </Space>
            </Divider>

            <Table
              dataSource={van.maintenanceHistory || []}
              rowKey={(record) => record._id || record.date}
              pagination={false}
            >
              <Column
                title="Date"
                dataIndex="date"
                key="date"
                render={(date) => formatDate(date)}
              />
              <Column
                title="Mileage"
                dataIndex="mileage"
                key="mileage"
              />
              <Column
                title="Next Service Mileage"
                dataIndex="nextServiceMileage"
                key="nextServiceMileage"
              />
              <Column
                title="Description"
                dataIndex="description"
                key="description"
              />
            </Table>

            {van.notes && (
              <>
                <Divider orientation="left">Notes</Divider>
                <Card>
                  <p>{van.notes}</p>
                </Card>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default VanDetail; 